/**
 * The asset map (F19).
 *
 * A tile-free scatter: each asset is a marker positioned by the percentage
 * coordinates the API projected, coloured by its type. This is deliberately
 * NOT a tiled geographic map — F19 asks to see where the portfolio's sites are
 * relative to each other and by country, which a projection answers without a
 * mapping dependency or a third-party tile server.
 *
 * The rules that shape it, from PRD §9.7 and §4.2:
 *
 *   - **Colour is the asset TYPE, a category — never sentiment.** A legend
 *     names every colour, and each marker carries its name and type in text
 *     (tooltip + the country list), so the plot is never colour-only.
 *   - **The plot is decorative; the data is the lists.** The scatter is
 *     `aria-hidden`, and the country breakdown and the marker roster below it
 *     are the accessible, screen-reader-legible form of the same information.
 *   - **An asset with no coordinates cannot be plotted**, so it is listed under
 *     "uncharted" rather than dropped or placed at a fake origin.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 10.5, PRD F19, §4.2, §9.7
 */
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import type { AssetMap } from '@nexus/contracts';

import { ASSET_TYPE_COLOR } from '../../components/domain/asset-taxonomy';
import { Card, CardBody, CardHead } from '../../components/ui/Card';
import { Empty } from '../../components/ui/Empty';
import { Kicker } from '../../components/ui/Kicker';
import { Note } from '../../components/ui/Note';
import { PageHead } from '../../components/ui/PageHead';
import { assetPath, ROUTES } from '../../lib/routes';

import { useAssetMap } from './api/assets.queries';

export function AssetMapPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { data, isPending, isError } = useAssetMap();

  if (isError) {
    return (
      <p role="alert" className="text-sem-bad">
        {t('errors.generic')}
      </p>
    );
  }

  return (
    <>
      <PageHead
        kicker={t('nav.group.pipeline')}
        title={t('asset.map.title')}
        lead={t('asset.map.lead')}
        actions={
          <Link to={ROUTES.assets} className="text-blue text-sm hover:underline">
            {t('asset.map.backToList')}
          </Link>
        }
      />

      {isPending ? (
        <p className="text-body" aria-busy="true">
          {t('state.loading')}
        </p>
      ) : (
        <MapContent map={data} />
      )}
    </>
  );
}

function MapContent({ map }: { map: AssetMap }): React.JSX.Element {
  const { t } = useTranslation();

  /* The asset types actually present, for a legend that names only what is on
     the plot rather than all eight. */
  const typesShown = [...new Set(map.points.map((p) => p.type))];

  return (
    <div className="flex flex-col gap-s4">
      <Card>
        <CardHead>
          <Kicker as="h2">{t('asset.map.plotTitle')}</Kicker>
        </CardHead>
        <CardBody>
          {map.points.length === 0 ? (
            <Empty
              title={t('asset.map.emptyTitle')}
              action={
                <Link to={ROUTES.assets} className="text-blue text-sm hover:underline">
                  {t('asset.map.backToList')}
                </Link>
              }
            >
              {t('asset.map.emptyBody')}
            </Empty>
          ) : (
            <>
              {/* Decorative: the roster and country lists below carry the same
                  facts in a screen-reader-legible form. */}
              <div
                className="bg-off-white relative w-full rounded-lg"
                style={{ aspectRatio: '16 / 9' }}
                aria-hidden="true"
              >
                {map.points.map((point) => (
                  <span
                    key={point.id}
                    title={`${point.name} — ${t(`asset.type.${point.type}`)}`}
                    className="absolute size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-white"
                    style={{
                      left: `${String(point.x)}%`,
                      top: `${String(point.y)}%`,
                      backgroundColor: ASSET_TYPE_COLOR[point.type],
                    }}
                  />
                ))}
              </div>

              <ul className="mt-s3 flex flex-wrap gap-x-4 gap-y-1.5">
                {typesShown.map((type) => (
                  <li key={type} className="flex items-center gap-1.5">
                    <span
                      aria-hidden="true"
                      className="size-2.5 rounded-full"
                      style={{ backgroundColor: ASSET_TYPE_COLOR[type] }}
                    />
                    <span className="text-body text-xs font-light">
                      {t(`asset.type.${type}`)}
                    </span>
                  </li>
                ))}
              </ul>

              <Note className="mt-s3">{t('asset.map.projectionNote')}</Note>
            </>
          )}
        </CardBody>
      </Card>

      {map.byCountry.length > 0 && (
        <Card>
          <CardHead>
            <Kicker as="h2">{t('asset.map.byCountryTitle')}</Kicker>
          </CardHead>
          <CardBody>
            <ul className="flex flex-col divide-y divide-gray-100">
              {map.byCountry.map((row) => (
                <li key={row.country} className="flex items-center justify-between py-2">
                  <span className="text-body text-sm font-light">{row.country}</span>
                  <span className="font-display text-navy-text text-sm font-extrabold tabular-nums">
                    {row.count}
                  </span>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      {map.uncharted.length > 0 && (
        <Card>
          <CardHead>
            <Kicker as="h2">{t('asset.map.unchartedTitle')}</Kicker>
          </CardHead>
          <CardBody>
            <ul className="flex flex-col divide-y divide-gray-100">
              {map.uncharted.map((asset) => (
                <li key={asset.id} className="flex items-center justify-between py-2">
                  <Link
                    to={assetPath(asset.id)}
                    className="text-navy-text text-sm font-light hover:underline"
                  >
                    {asset.name}
                  </Link>
                  <span className="text-body text-xs font-light">
                    {t(`asset.type.${asset.type}`)}
                  </span>
                </li>
              ))}
            </ul>
            <Note className="mt-s3">{t('asset.map.unchartedNote')}</Note>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
